import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import { useSiteContext } from "../../contexts/SiteProvider";
import { showAlertAutoClose } from "../../services/notifyService";
import { post } from "../../services/smartApiService";
import { AI_PROMPTS_URLS } from "../../api/ManageUrls";
import { SdsForm } from "soft_digi";
import { SdsFormElementProps } from "soft_digi/dist/sdforms/SdsFromInterFace";

const schema = Yup.object({
  name: Yup.string()
    .required("Prompt name is required")
    .max(15, "Name cannot exceed 15 characters")
    .matches(
      /^[A-Z0-9_]+$/,
      "Name must contain only uppercase letters, numbers, and underscores"
    ),
  prompt_text: Yup.string().required("Prompt text is required"),
  description: Yup.string().nullable(),
  placeholders: Yup.string().nullable(),
  is_active: Yup.object().shape({
    label: Yup.string(),
    value: Yup.boolean(),
  }),
});

interface AIPromptsFormProps {
  loadTableData: () => void;
  data?: any;
}

const AIPromptsForm: React.FC<AIPromptsFormProps> = ({ loadTableData, data }) => {
  // Format default values to match SELECT_BOX format
  const formatDefaultValues = (data: any) => {
    if (!data) return { is_active: { label: "Active", value: true } };

    return {
      ...data,
      is_active: data.is_active !== undefined
        ? { label: data.is_active ? "Active" : "Inactive", value: data.is_active }
        : { label: "Active", value: true },
    };
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<any>({
    resolver: yupResolver(schema),
    defaultValues: formatDefaultValues(data),
  });

  const { closeModal } = useSiteContext();

  const onSubmit = (_data: any) => {
    const isUpdate = data && data.id;

    // Transform the is_active from {label, value} to boolean
    const payload = {
      ..._data,
      is_active: _data.is_active?.value !== undefined ? _data.is_active.value : true,
    };

    const request$ = isUpdate
      ? post(`${AI_PROMPTS_URLS.UPDATE}${data.id}`, payload)
      : post(AI_PROMPTS_URLS.INSERT, payload);

    const subscription = request$.subscribe(
      () => {
        showAlertAutoClose(
          `AI Prompt ${isUpdate ? "updated" : "saved"} successfully`,
          "success"
        );
        reset();
        closeModal();
        loadTableData();
      },
      (error) => {
        showAlertAutoClose(
          error?.error?.message || "Failed to save AI prompt",
          "error"
        );
      }
    );

    return () => subscription.unsubscribe();
  };

  const promptElements: SdsFormElementProps[] = [
    {
      type: "TEXT_BOX",
      width: 12,
      name: "name",
      element: {
        label: "Prompt Name",
        placeHolder: "e.g., STOCK_ANALYSIS",
        isRequired: true,
        inputType: "BORDER_LABEL",
        max: 15,
      },
    },
    {
      type: "TEXTAREA",
      width: 12,
      name: "prompt_text",
      element: {
        label: "Prompt Text",
        placeHolder: "Enter prompt text with placeholders like {company_name}, {sector}, etc.",
        isRequired: true,
        rows: 5,
        inputType: "BORDER_LABEL",
      },
    },
    {
      type: "TEXTAREA",
      width: 12,
      name: "description",
      element: {
        label: "Description",
        placeHolder: "Enter description of what this prompt does",
        rows: 3,
        inputType: "BORDER_LABEL",
      },
    },
    {
      type: "TEXT_BOX",
      width: 12,
      name: "placeholders",
      element: {
        label: "Placeholders",
        placeHolder: "e.g., company_name,sector,date",
        inputType: "BORDER_LABEL",
      },
    },
    {
      type: "SELECT_BOX",
      width: 12,
      name: "is_active",
      element: {
        label: "Status",
        placeHolder: "Select status",
        inputType: "BORDER_LABEL",
        options: [
          { label: "Active", value: true },
          { label: "Inactive", value: false },
        ],
      },
    },
    {
      type: "BUTTON",
      width: 6,
      name: "cancel",
      class_name: "",
      element: {
        label: "Cancel",
        classList: ["button"],
        onClick: () => closeModal(),
      },
    },
    {
      type: "BUTTON",
      width: 6,
      name: "submit",
      class_name: "has-text-right",
      element: {
        label: data ? "Update" : "Save",
        classList: ["button", "is-primary"],
        onClick: handleSubmit(onSubmit),
      },
    },
  ];

  return (
    <div className="">
      <SdsForm
        elements={promptElements}
        control={control}
        errors={errors}
      />
    </div>
  );
};

export default AIPromptsForm;
